<template>
    <div>
        <div class="sm:mt-8 sm:mx-auto sm:w-full h-screen sm:h-auto sm:max-w-md">
            <vue-turnstile :site-key=siteKey v-model="reCaptchaToken" v-if="turnstileLoaded" />

            <div class="pt-14 pb-9 sm:shadow-card sm:rounded-xl h-full sm:h-auto bg-white px-6 sm:px-10">
                <auth-head class="mb-10" />

                <button
                    @click="handleSocialLogin"
                    type="button"
                    class="mt-[38px]"
                    :class="isSocialMediaBrowser ?  fadedSocialButtonStyle : socialButtonStyle"
                    :disabled="isSocialMediaBrowser"
                >
                    <GoogleIcon class='mr-2' :style="isSocialMediaBrowser ? 'filter: grayscale(100%);' : false"/>
                    Log in with Google
                </button>

                <div 
                    v-if="isSocialMediaBrowser" 
                    class="bg-[#FFD064] mt-4 flex items-center rounded-lg border-[2px] border-[#FFD064] "
                >
                    <Warning class="m-2 h-auto w-20"/>
                    <p class="bg-[#FBEECD] rounded-r-lg p-4 text-sm ">
                        To sign in with Google, tap the 3 dots (⋮) & select ‘Open in Browser’”
                    </p>
                </div>
            
                <divider class="mt-4" />

                <form
                    class="space-y-6"
                    action="#"
                    @submit.prevent="handleLogin"
                    method="POST"
                >
                    <text-element
                        idKey="email"
                        type="email"
                        required
                        autocomplete="email"
                        v-model="email"
                        @keyup.enter="handleLogin"
                        placeholder="Email"
                        :errors="errors.email || null"
                    >
                        <label
                            for="email"
                            class="text-sm font-medium won-label-text-color mb-2 block"
                        >
                            Email
                        </label>
                    </text-element>

                    <text-element
                        idKey="password"
                        :type="showPassword ? 'text' : 'password'"
                        required
                        autocomplete="current-password"
                        v-model="password"
                        placeholder="Password"
                        @keyup.enter="handleLogin"
                        :errors="errors.password || null"
                    >
                        <label
                            for="password"
                            class="text-sm font-medium won-label-text-color mb-2 block"
                        >
                            Password
                        </label>

                        <template #inputIcon>
                            <ShowPasswordTrigger
                                @trigger-show="showPasswordTriggerHandler"
                                :isOpen="showPassword"
                            />
                        </template>
                    </text-element>

                    <router-link to="/forgot-password">
                        <span
                            class="font-bold underline text-won-green mt-2 w-fit text-[14px] leading-[17px] underline-offset-2"
                        >
                            Forgot your password?
                        </span>
                    </router-link>

                    <div>
                        <button
                            @click="handleLogin"
                            type="button"
                            :disabled="isDisabled"
                            :class="[ 
                                {
                                    'won-green-button-color': !isDisabled,
                                    'won-green-button-color-disabled': isDisabled,
                                },
                                buttonStyle
                            ]"
                        >
                            <span class="flex gap-2 items-center">
                                <span class="-ml-6" :class="loading ? 'animate-spin inline' : 'hidden'"> 
                                    <LoadingIcon />
                                </span>
                                
                                <span>
                                    Login
                                </span>
                            </span>
                        </button>

                        <p
                            class="font-bold text-center text-won-blue mt-4  text-[14px] leading-[17px]"
                        >
                            Don't have an account?
                            <router-link
                                class="underline text-won-green mt-2 w-fit underline-offset-2"
                                to="/register"
                            >
                                Register Now
                            </router-link>
                        </p>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import {ref, computed, watch, shallowRef, onMounted} from "vue";
import { useRouter } from "vue-router";
import { setAuthState } from "../../composables/auth";
import { useNotifications } from "../../store/notifications.store"

import { buttonStyle, socialButtonStyle,fadedSocialButtonStyle } from "../../utils/styles";
import { validateEmail, formatErrors, popupwindow, isSocialMediaAppBrowser } from "../../utils/helpers";

import TextElement from "../../forms/elements/text";
import ShowPasswordTrigger from "../../components/utils/show-password-trigger.vue"
import AuthHead from "../../components/auth-head";
import Divider from "../../components/divider";
import GoogleReCaptchaV3 from "../../components/googlerecaptchav3/GoogleReCaptchaV3";
import VueTurnstile from "vue-turnstile";

import GoogleIcon from "../../icons/google";
import Warning from "../../icons/warning.vue";
import LoadingIcon from "../../icons/loading.vue";

let { authenticate, setUserData } = setAuthState();

export default {
    setup() {
        // set default values
        let email = ref("");
        let password = ref("");
        let loading = ref(false);
        
        const siteKey = process.env.MIX_TURNSTILE_SITE_KEY
        const turnstileLoaded = ref(false);
        const showPassword = shallowRef(false)
        
        const captcha = ref(null);
        const reCaptchaToken = ref("");
        const reCaptchaGenerated = ref(true);
        const isSocialMediaBrowser = isSocialMediaAppBrowser();
        const ua = navigator.userAgent.toLowerCase();

        const errors = ref({});
        const router = useRouter();

        const notificationStore = useNotifications()

        watch(reCaptchaToken, (val) => {
            localStorage.setItem("reCaptchaToken", val);
        });

        const showPasswordTriggerHandler = () => {
            showPassword.value = !showPassword.value
        }

        const handleSocialLogin = () => {
            popupwindow("/google/login", "Google Login", 500, 600);

            window.onmessage = function ({ data: { user, type, newly_registered_user } }) {
                if (type === "social") {
                    setUserData(user);

                    window.dataLayer = window.dataLayer || [];
                    
                    if(newly_registered_user) {
                        window.dataLayer.push({
                            'event': 'register_account',
                            'user_id': user?.id
                        });
                    } 
                    
                    if(!newly_registered_user) {
                        window.dataLayer.push({
                            'event': 'user_login',
                            'user_id': user?.id
                        });
                    }
                  
                    router.push({ name: 'home' });
                }
            }
        };

        const handleLogin = async () => {
            if (!email.value.length || !password.value.length) {
                notificationStore.notify('Enter credentials!', 'warning')
                return;
            }

            loading.value = true;

            authenticate({ email: email.value, password: password.value })
                .then((res) => {
                    errors.value = {};

                    window.dataLayer = window.dataLayer || []; 
                    window.dataLayer.push({
                        'event': 'user_login',
                        'user_id': res?.id
                    });
                    
                    router.push({ name: 'home' });
                })
                .catch(async ({ response: { data } }) => {
                    if (data.errors) {
                        errors.value = formatErrors(data.errors);
                    } else {
                        notificationStore.notify(data.message ?? "Invalid request!", "error")
                    }
                    // refresh turnstyle
                    const newToken = await window.turnstile?.getResponse();
                    reCaptchaToken.value = newToken;
                })
                .finally(() => {
                    reCaptchaGenerated.value = false;
                    loading.value = false;
                });
        };

        const isDisabled = computed(() => {
            if (loading.value) return false;
            return (
                !email.value || !validateEmail(email.value) || !password.value
            );
        });

        onMounted(() => {
            turnstileLoaded.value = true;
        });

        return {
            ua,
            email,
            password,
            errors,
            isSocialMediaBrowser,
            captcha,
            reCaptchaToken,
            handleLogin,
            handleSocialLogin,
            showPasswordTriggerHandler,
            buttonStyle,
            socialButtonStyle,
            fadedSocialButtonStyle,
            isDisabled,
            showPassword,
            loading,
            siteKey,
            turnstileLoaded
        };
    },
    components: {
        VueTurnstile,
        Warning,
        GoogleIcon,
        GoogleReCaptchaV3,
        AuthHead,
        Divider,
        TextElement,
        ShowPasswordTrigger,
        LoadingIcon
    },
};
</script>
